import { isNavTarget, type NavTarget } from './components/luna-common';

export type ShellVariant = 'home-nav' | 'picker-back' | 'puzzle-bar' | 'auth' | 'none';

export interface PresetRowData {
  presetId: number;
  sizeLabel: string;
  detail?: string;
}

const BOARD_ROUTE = /^\/puzzles\/[^/]+\/boards$/;
const PUZZLE_ROUTE = /^\/puzzles\/[^/]+(\/[^/]+)?$/;

function normalisePath(path: string): string {
  const value = path || '/';
  if (value.length > 1 && value.endsWith('/')) {
    return value.slice(0, -1);
  }
  return value;
}

/** Resolve the shell variant for a route path (BR-41/42). */
export function resolveShellVariant(path: string): ShellVariant {
  const value = normalisePath(path);
  if (value === '/' || value === '/programme') {
    return 'home-nav';
  }
  if (value === '/appearance') {
    return 'puzzle-bar';
  }
  if (value === '/login' || value.startsWith('/auth/')) {
    return 'auth';
  }
  if (BOARD_ROUTE.test(value)) {
    return 'picker-back';
  }
  if (PUZZLE_ROUTE.test(value)) {
    return 'puzzle-bar';
  }
  return 'none';
}

/** Nav items for the home shell: home, programme and appearance (BR-79). */
export function resolveNavItems(): NavTarget[] {
  return ['home', 'programme', 'appearance'];
}

/**
 * Split a wasm engine preset name into size-label + detail (BR-49). A leading
 * `N × N` / `N x N` token becomes the size-label; the trimmed remainder becomes
 * the detail; otherwise the whole name is the size-label.
 */
export function splitPresetLabel(name: string): { sizeLabel: string; detail?: string } {
  const trimmed = (name ?? '').trim();
  const match = /^(\d+\s*[×x]\s*\d+)\s*(.*)$/.exec(trimmed);
  if (!match) {
    return { sizeLabel: trimmed };
  }
  const detail = match[2].trim().replace(/^[,\s]+/, '');
  return detail ? { sizeLabel: match[1], detail } : { sizeLabel: match[1] };
}

/** True when a NavTarget is usable in the U007 home shell. */
export function isShellNavTarget(value: string): value is NavTarget {
  return isNavTarget(value);
}
